// SPDX-License-Identifier: MIT
//
// genesis-web half of the "Connect my Claude" SSO handoff to genesis-mcp.
//
// genesis-mcp's /authorize (frozen, cookie-based) redirects an unauthenticated
// user to `${webBase}/?return_to=<mcp /authorize URL>`. genesis-web stores its
// Supabase session in localStorage (not a cross-subdomain cookie), so we use a
// token-in-redirect handoff instead of a Domain=.myconciergerie.fr cookie :
// once the user is authenticated, mint a one-time code (issue_handoff_code rpc)
// and bounce back to return_to with &handoff_code=<code>. genesis-mcp consumes
// the code (service-role, atomic) to learn the user_id.
//
// See memory mcp_authorize_session_handoff_gap + the 2026-05-21T0500 migration.

import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { getSession } from './session';

// The ONLY origin+path genesis-web will hand a session off to. Hardcoded (NOT
// env-driven) so it can never be misconfigured into an open redirect — a
// foreign `return_to` would otherwise let an attacker harvest a one-time code.
const MCP_ORIGIN = 'https://mcp.genesis.myconciergerie.fr';
const MCP_AUTHORIZE_PATH = '/authorize';

const PENDING_KEY = 'genesis.mcp_handoff';
const PENDING_TTL_MS = 15 * 60 * 1000; // survives a magic-link email round-trip

/** Strict allowlist: only the mcp /authorize endpoint, https, exact path. */
export function isAllowedReturnTo(returnTo: string): boolean {
  try {
    const u = new URL(returnTo);
    return u.origin === MCP_ORIGIN && u.pathname === MCP_AUTHORIZE_PATH;
  } catch {
    return false;
  }
}

/**
 * Mint a one-time handoff code bound to the originating request's PKCE
 * code_challenge. The binding lets genesis-mcp reject a leaked code replayed
 * with a different PKCE pair (closes the leak-window takeover vector).
 */
export async function issueHandoffCode(codeChallenge: string): Promise<string> {
  const { data, error } = await supabase.rpc('issue_handoff_code', { p_code_challenge: codeChallenge });
  if (error) throw error;
  if (typeof data !== 'string' || !data) {
    throw new Error('issue_handoff_code returned no code');
  }
  return data;
}

/** Issue a code and redirect back to the mcp /authorize URL. Guarded. */
export async function completeMcpHandoff(returnTo: string): Promise<void> {
  if (!isAllowedReturnTo(returnTo)) return; // anti open-redirect — never issue a code for a foreign target
  const url = new URL(returnTo);
  // Bind the code to the PKCE challenge carried in the mcp /authorize URL.
  const codeChallenge = url.searchParams.get('code_challenge');
  if (!codeChallenge) return; // a real mcp bounce always carries S256 code_challenge; bail if malformed
  const code = await issueHandoffCode(codeChallenge);
  url.searchParams.set('handoff_code', code);
  window.location.replace(url.toString());
}

/** Persist a pending handoff across a sign-in round-trip (incl. magic-link). */
export function stashPendingHandoff(returnTo: string): void {
  if (!isAllowedReturnTo(returnTo)) return;
  localStorage.setItem(
    PENDING_KEY,
    JSON.stringify({ returnTo, expiresAt: Date.now() + PENDING_TTL_MS }),
  );
}

/** Read the pending handoff, validating TTL + allowlist; null if none/stale. */
export function readPendingHandoff(): string | null {
  const raw = localStorage.getItem(PENDING_KEY);
  if (!raw) return null;
  try {
    const { returnTo, expiresAt } = JSON.parse(raw) as { returnTo?: unknown; expiresAt?: unknown };
    if (
      typeof returnTo !== 'string' ||
      typeof expiresAt !== 'number' ||
      Date.now() > expiresAt ||
      !isAllowedReturnTo(returnTo)
    ) {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return returnTo;
  } catch {
    localStorage.removeItem(PENDING_KEY);
    return null;
  }
}

export function clearPendingHandoff(): void {
  localStorage.removeItem(PENDING_KEY);
}

/**
 * Resume a stashed handoff once authenticated. Consumes (clears) the pending
 * entry BEFORE redirecting so it cannot double-fire across the landing path
 * and the observeSession callback. Returns true if it redirected.
 */
export async function tryResumePendingHandoff(session?: Session | null): Promise<boolean> {
  const returnTo = readPendingHandoff();
  if (!returnTo) return false;
  const s = session !== undefined ? session : await getSession();
  if (!s) return false; // not signed in yet — keep pending for the next session event
  clearPendingHandoff();
  await completeMcpHandoff(returnTo);
  return true;
}
